'use client';

import type { Breadcrumb } from '@eliadmin/core/src/api/products/types/product-types';
import { BREADCRUMBS_CONFIG } from '@eliadmin/core/src/constants/breadcrumbs-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  arePathsEqual,
  getFlatBreadcrumbs,
  getSlugFromPage,
} from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { FiHome } from 'react-icons/fi';

export type BreadcrumbsProps = {
  className?: {
    wrapper?: string;
    list?: string;
    item?: string;
    itemActive?: string;
    link?: string;
  };
  homeText?: string;
  breadcrumb: Breadcrumb;
  product?: { name: string };
};

export function Breadcrumbs({ breadcrumb, className, product }: BreadcrumbsProps) {
  const pathname = usePathname();

  const breadcrumbs = getFlatBreadcrumbs(breadcrumb);

  if (BREADCRUMBS_CONFIG.noDisplay.filter((el: string) => el === pathname).length) {
    return <nav className="hidden"></nav>;
  }

  return (
    <nav className={cn('w-full rounded-none bg-transparent', 'ea-breadcrumbs', className?.wrapper)}>
      <ol
        className={cn(
          'flex flex-row flex-wrap items-center space-x-1 text-sm font-light text-primary',
          'ea-breadcrumbs__list',
          className?.list,
        )}
      >
        {breadcrumbs.map((breadcrumb, i) => (
          <li key={breadcrumb.slug} className="w-auto">
            <span
              className={cn(className?.item, 'text-sm font-light text-border', i === 0 && 'hidden')}
            >
              /{' '}
            </span>
            <span
              className={cn(
                {
                  [cn(
                    'pointer-events-none',
                    'ea_breadcrumbs__item--active',
                    className?.itemActive,
                  )]: arePathsEqual(pathname, breadcrumb.slug),
                },
                'ea_breadcrumbs__item',
                className?.item,
              )}
            >
              <Link
                href={breadcrumb.slug ?? '#'}
                className={cn('ea-breadcrumbs__link', className?.link)}
                prefetch={false}
              >
                {breadcrumb.name}
              </Link>
            </span>
          </li>
        ))}
        {product && (
          <li className="flex items-center space-x-1" aria-current="page">
            <span
              aria-hidden="true"
              className={cn(className?.item, 'text-sm font-light text-border')}
            >
              /{' '}
            </span>
            <span className="text-sm font-medium text-black">{product.name}</span>
          </li>
        )}
      </ol>
    </nav>
  );
}
